import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Users, Send, Grid, Award, CreditCard, LifeBuoy, ArrowRight } from 'lucide-react';
import { API_URLS } from '../api';
import { Header } from '../components/Header';
import { Seo } from '../components/Seo';

const inquiryOptions = [
  { value: '1', label: 'I do not understand the amount on my bank statement' },
  { value: '2', label: 'I wish to receive the invoice of my order' },
  { value: '3', label: 'I wish to cancel' },
  { value: '4', label: 'I want to unsubscribe' },
  { value: '5', label: 'Other' }
];

export const Home = () => {
  // Form State Management
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    iban: '',
    inquiryType: '1',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const selectedOption = inquiryOptions.find(option => option.value === formData.inquiryType);
    const payload = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      iban_last_5: formData.iban,
      is_unsubscribe: false,
      message: formData.message,
      options: {
        reason: selectedOption?.label ?? ''
      }
    };

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch(API_URLS.unsubscribe, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      setSubmitStatus('success');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        iban: '',
        inquiryType: '1',
        message: ''
      });
    } catch (error) {
      console.error('Form submission failed:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="font-sans bg-[#Fcfcfc] text-gray-900 min-h-screen">
      <Seo
        title="Cancel Your Subscription Online | Futureworkhelp"
        description="Cancel your subscription online with Futureworkhelp. Contact our support team for subscription questions, billing assistance, and cancellation help."
        url="https://futureworkhelp.net/"
        imageAlt="Futureworkhelp subscription cancellation and customer support"
      />

      <Header />

      {/* 1. HERO & EMBEDDED FORM */}
      <section id="contact" className="relative pt-32 lg:pt-40 pb-16 lg:pb-32 px-4 sm:px-6 lg:px-8 overflow-hidden min-h-[90vh] lg:min-h-screen flex items-center">
        {/* Abstract Background Elements */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[50rem] bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-red-100/50 via-white to-white opacity-80 pointer-events-none"></div>
          <div className="absolute top-1/4 left-0 w-96 h-96 bg-red-400/10 rounded-full blur-[100px] mix-blend-multiply animate-pulse"></div>
          <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-red-600/10 rounded-full blur-[100px] mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.03] pointer-events-none"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto w-full grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* LEFT: TEXT & TRUST */}
          <div className="text-center lg:text-left pt-10 lg:pt-0">
            <h1 className="text-4xl sm:text-5xl lg:text-[4rem] font-extrabold text-gray-900 leading-[1.1] mb-6 tracking-tight">
              Cancel your subscription
              <span className="relative inline-block mt-2">
                <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-400">
                  Free &amp; Immediately
                </span>
                <div className="absolute -bottom-2 left-0 w-full h-4 bg-red-200/50 -skew-x-12"></div>
              </span>
              <span className="mt-4 block text-xl sm:text-2xl font-semibold text-gray-700 tracking-tight">
                through our online cancellation service.
              </span>
            </h1>
            <div className="space-y-4 mb-10 border-l-4 border-red-500 pl-6 text-left">
              <p className="text-lg text-gray-600 font-medium leading-relaxed">
                Our operators are trained by our partners to answer all your questions quickly and efficiently.
              </p>
              <p className="text-base text-gray-500 leading-relaxed">
                The subscription services that we provide offer our customers a unique solution to multiple problems.
              </p>
            </div>
          </div>

          {/* RIGHT: THE FORM */}
          <div className="relative w-full max-w-xl mx-auto lg:mx-0 lg:ml-auto mt-6 lg:mt-0">
            <div className="absolute inset-0 bg-gradient-to-tr from-red-100 to-red-50 rounded-[2rem] lg:rounded-[2.5rem] transform translate-x-3 translate-y-3 sm:translate-x-6 sm:translate-y-6 -z-10"></div>
            <div className="bg-white/90 backdrop-blur-2xl border border-white p-6 sm:p-10 lg:p-12 rounded-[2rem] lg:rounded-[2.5rem] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.05)] relative z-10">
              <p className="text-2xl font-bold text-gray-900 mb-6 lg:mb-8 tracking-tight">Contact us</p>

              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
                <div className="grid md:grid-cols-2 gap-4 sm:gap-5">
                  <div className="group relative">
                    <div className="absolute inset-y-0 left-0 pl-4 sm:pl-5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-red-600 transition-colors">
                      <Users className="w-5 h-5" />
                    </div>
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} required className="w-full pl-12 sm:pl-14 pr-4 sm:pr-5 py-4 sm:py-5 bg-white border-2 border-gray-100 rounded-[1rem] sm:rounded-[1.25rem] focus:border-red-600 outline-none transition-all text-[15px] sm:text-[16px] font-semibold text-gray-900 placeholder-gray-400 hover:border-gray-200 shadow-sm" placeholder="First Name" />
                  </div>
                  <div className="group relative">
                    <div className="absolute inset-y-0 left-0 pl-4 sm:pl-5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-red-600 transition-colors">
                      <Users className="w-5 h-5" />
                    </div>
                    <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} required className="w-full pl-12 sm:pl-14 pr-4 sm:pr-5 py-4 sm:py-5 bg-white border-2 border-gray-100 rounded-[1rem] sm:rounded-[1.25rem] focus:border-red-600 outline-none transition-all text-[15px] sm:text-[16px] font-semibold text-gray-900 placeholder-gray-400 hover:border-gray-200 shadow-sm" placeholder="Last Name" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 sm:gap-5">
                  <div className="group relative">
                    <div className="absolute inset-y-0 left-0 pl-4 sm:pl-5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-red-600 transition-colors">
                      <Send className="w-5 h-5" />
                    </div>
                    <input type="email" name="email" value={formData.email} onChange={handleInputChange} required className="w-full pl-12 sm:pl-14 pr-4 sm:pr-5 py-4 sm:py-5 bg-white border-2 border-gray-100 rounded-[1rem] sm:rounded-[1.25rem] focus:border-red-600 outline-none transition-all text-[15px] sm:text-[16px] font-semibold text-gray-900 placeholder-gray-400 hover:border-gray-200 shadow-sm" placeholder="Email Address" />
                  </div>
                  <div className="group relative">
                    <div className="absolute inset-y-0 left-0 pl-4 sm:pl-5 flex items-center pointer-events-none text-gray-400 group-focus-within:text-red-600 transition-colors">
                      <CreditCard className="w-5 h-5" />
                    </div>
                    <input type="text" name="iban" maxLength={5} value={formData.iban} onChange={handleInputChange} className="w-full pl-12 sm:pl-14 pr-4 sm:pr-5 py-4 sm:py-5 bg-white border-2 border-gray-100 rounded-[1rem] sm:rounded-[1.25rem] focus:border-red-600 outline-none transition-all text-[15px] sm:text-[16px] font-semibold text-gray-900 placeholder-gray-400 hover:border-gray-200 shadow-sm" placeholder="Last 5 IBAN digits" />
                  </div>
                </div>

                <div className="group relative z-10">
                  <label htmlFor="reason-for-contact" className="mb-2 block text-sm font-bold text-gray-700">
                    Reason for contact <span aria-hidden="true">*</span>
                  </label>
                  <select id="reason-for-contact" name="inquiryType" value={formData.inquiryType} onChange={handleInputChange} required className="w-full px-5 sm:px-6 pr-10 py-4 sm:py-5 bg-white border-2 border-gray-100 rounded-[1rem] sm:rounded-[1.25rem] focus:border-red-600 outline-none transition-all text-[15px] sm:text-[16px] font-semibold text-gray-900 appearance-none cursor-pointer relative z-0 hover:border-gray-200 shadow-sm">
                    {inquiryOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.value} - {option.label}</option>
                    ))}
                  </select>
                  <div className="absolute top-7 bottom-0 right-5 sm:right-6 flex items-center pointer-events-none text-gray-400 z-10">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7"></path></svg>
                  </div>
                </div>

                <div className="group relative">
                  <textarea rows={3} name="message" value={formData.message} onChange={handleInputChange} className="w-full px-5 sm:px-6 py-4 sm:py-5 bg-white border-2 border-gray-100 rounded-[1rem] sm:rounded-[1.25rem] focus:border-red-600 outline-none transition-all resize-none text-[15px] sm:text-[16px] font-semibold text-gray-900 placeholder-gray-400 hover:border-gray-200 shadow-sm" placeholder="Briefly describe how we can help..."></textarea>
                </div>

                <div className="pt-2 sm:pt-4">
                  <button type="submit" disabled={isSubmitting} className="w-full py-4 sm:py-5 px-2 bg-red-600 hover:bg-red-700 disabled:bg-red-300 disabled:hover:translate-y-0 disabled:cursor-not-allowed text-white rounded-[1rem] sm:rounded-[1.25rem] text-[15px] sm:text-[18px] font-extrabold tracking-wide flex items-center justify-center gap-2 sm:gap-3 transition-all active:scale-[0.98] shadow-[0_10px_30px_rgba(220,38,38,0.25)] hover:shadow-[0_15px_40px_rgba(220,38,38,0.4)] hover:-translate-y-1 group/btn border-2 border-red-600">
                    <span className="whitespace-nowrap uppercase">{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                    <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 group-hover/btn:translate-x-1.5 transition-transform flex-shrink-0" />
                  </button>
                </div>

                {submitStatus === 'success' && (
                  <p className="text-sm font-semibold text-green-600 text-center">Your request has been submitted successfully.</p>
                )}

                {submitStatus === 'error' && (
                  <p className="text-sm font-semibold text-red-600 text-center">Unable to submit your request. Please try again.</p>
                )}
              </form>
            </div>
          </div>

        </div>
      </section>

      {/* 2. PREMIUM 2x2 GRID (WHY CHOOSE US) */}
      <section id="how-it-works" className="bg-gray-50 py-16 lg:py-32 border-t border-gray-200 relative overflow-hidden">
        <div className="absolute top-0 right-0 -mr-32 -mt-32 w-[600px] h-[600px] rounded-full bg-red-600/5 blur-[120px] pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 -ml-32 -mb-32 w-[600px] h-[600px] rounded-full bg-gray-400/10 blur-[120px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

          <div className="text-center max-w-4xl mx-auto mb-16 lg:mb-24">
            <h2 className="text-4xl sm:text-5xl lg:text-7xl font-black text-gray-900 mb-8 tracking-tighter leading-tight">
              How <span className="relative inline-block text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-400">
                <span className="notranslate" translate="no">futureworkhelp</span>
                <div className="absolute -bottom-2 left-0 w-full h-3 bg-red-200/50 -skew-x-12"></div>
              </span> makes it easy
            </h2>
          </div>

          <div className="space-y-12 lg:space-y-0 lg:grid lg:grid-cols-3 gap-8 relative pb-20">
            {/* Background decorative path */}
            <div className="hidden lg:block absolute top-1/2 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-red-300 to-transparent -translate-y-1/2 z-0 opacity-50"></div>

            {[
              { icon: Grid, title: 'Simplified management of your subscriptions', desc: 'futureworkhelp offers you to easily manage different subscriptions. Fill in the e-mail address you used when you subscribed and reach your customer account in one click. All the essential information is there to give you maximum control of your subscriptions. You can also contact the agents to ask for help, unsubscribe or make a complaint.' },
              { icon: LifeBuoy, title: 'Agents at your service', desc: 'futureworkhelp agents are experts in the after-sales services and have all the answers to your questions. We work 7 days per week, so you can contact the agents by live chat or by phone via our toll free number any time you need. You can also send them a message to which they will respond within 48 hours. If you have problems in managing your subscription on ports or in case you have any other question, contact us.' },
              { icon: Award, title: 'Recognized expertise', desc: 'We provide the after-sales service of several partner sites, which are all satisfied with our services. Our expertise provides the support for every single client. Our desire to ease the customer\'s life and our commitment to its satisfaction are the engines of futureworkhelp.' }
            ].map((card, i) => (
              <div key={i} className={`relative group z-10 lg:transform ${i === 0 ? 'lg:-translate-y-8' : i === 1 ? 'lg:translate-y-8' : 'lg:-translate-y-8'}`}>
                {/* Permanent Neo-brutalist shadow */}
                <div className="absolute inset-0 bg-red-100 group-hover:bg-red-600 rounded-[2rem] transform translate-y-3 translate-x-3 transition-colors duration-300 ease-out"></div>

                <div className="relative h-full bg-white bg-gradient-to-br from-white to-red-50/30 border-2 border-red-100 group-hover:border-red-600 p-8 sm:p-10 rounded-[2rem] shadow-[0_15px_40px_-15px_rgba(220,38,38,0.15)] group-hover:-translate-y-1 group-hover:-translate-x-1 transition-all duration-300 ease-out flex flex-col overflow-hidden">

                  {/* Big Number Watermark */}
                  <div className="absolute -top-6 -right-2 text-[10rem] font-black text-transparent bg-clip-text bg-gradient-to-b from-red-100 to-transparent opacity-80 select-none pointer-events-none group-hover:from-red-200 transition-all duration-300">
                    0{i + 1}
                  </div>

                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-100 to-white flex items-center justify-center mb-8 text-red-600 group-hover:scale-110 transition-transform duration-300 shadow-sm relative z-10 border border-red-200">
                    <card.icon className="w-8 h-8" />
                  </div>

                  <h3 className="text-2xl font-extrabold text-gray-900 mb-5 tracking-tight relative z-10 leading-snug">{card.title}</h3>
                  <p className="text-gray-600 text-[15px] font-medium leading-relaxed relative z-10 flex-grow">{card.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Professional Footer */}
      <footer className="bg-white border-t border-gray-200 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="notranslate flex items-center gap-2" translate="no">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-600">
                <Shield className="h-5 w-5 text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight text-gray-900">futureworkhelp.net</span>
            </div>
            <div className="flex gap-6 text-sm font-medium text-gray-500">
              <Link to="/" className="hover:text-red-600 transition-colors">Home</Link>
              <Link to="/faq/" className="hover:text-red-600 transition-colors">FAQ</Link>
            </div>
            <p className="text-sm text-gray-400 font-medium">
              &copy; {new Date().getFullYear()} <span className="notranslate" translate="no">futureworkhelp.net</span>. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

    </div>
  );
};
